import {Box, Divider} from '@mui/material';
import React, {Dispatch, SetStateAction, useEffect, useRef} from 'react';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as PlusIcon} from '../../../../assets/images/plus-icon.svg';
import {ReactComponent as TagTreeIcon} from '../../../../assets/images/tag-tree.svg';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import './CodingForm.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  useCodeSameAsPrevious,
  useDocumentCodingForm,
  useDocumentCodingFormList,
  useGetEditCodingDetails,
  usePinCodingForm,
} from '../../../../hooks/document/useDocument';
import {CodingFormItem, ISelectItem} from '../../../../shared/types';
import {
  IDocumentViewerCodingFormListResponse,
  ISaveCodingFormRequest,
} from '../../../../api/types/document.type';
import {RootState} from '../../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import DynamicFormFields, {
  type DynamicFormFieldsHandle,
} from '../../../coding-forms-management/create-edit-coding-form/preview-coding-forms/DynamicFormFields';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  storeCodingFormList,
  storeSetIsPinCodingFormSelected,
  setActiveDocument,
  setCompletedDocuments,
  removeCompletedDocuments,
} from '../../docViewerUtils';
import {
  saveCodingFormData,
  getMiniDocListGridData,
  getDocIsCompleted,
} from '../../../../api/endpoints/documentApi';
import {ToastType} from '../../../../shared/constants';
import {
  MiniDocRowSelection,
  prepareMiniDocDataRequest,
} from '../../doc-list/DocViewDocListDataGrid';
import {
  DocCompletedCheckResponse,
  MiniDocListResponse,
} from '../../../../shared/interfaces';
import {IDocViewDocListRowData} from '../../doc-list/interfaces';
import ArrBanner from '../../../common/arr-banner/ArrBanner';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  setCodeSameAsDocumentCodingForm,
  setIsCodeSameAsPrevious,
  setSaveCodingFormResponse,
  storeSourceTypeCode,
  setIsDocumentCompleted,
  incrementDocAnalyticsRefreshKey,
  triggerAssignmentProgressRefresh,
  setActiveDoc,
  removeCodeSameAsDocs,
  clearCodeSameAsDocsId,
} from '../../../../redux/slice/docViewSlice';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {useSignalStore} from '../../../PortalPopout/hooks/useSignalStore';

export interface CodingStampItem {
  CodingFormStampColorCode: string;
  CodingFormStampColorLabel: string;
  CodingFormStampColorRGB: string;
  CodingFormUserStampID: number;
  CodingFormID: number;
  IsDisabled: boolean;
  SourceID: number;
  CodingFormStampColorName: string;
}
export enum CodingFormPinLabel {
  default = 'DEFAULT',
  pin = 'PIN',
  unpin = 'UNPIN',
  pinAgain = 'PIN_AGAIN',
}
export interface CodingFormItems {
  id: number;
  ProjectFieldName: string;
  ProjectFieldDatatype: string | null;
  Sequence: number;
  ContextType: string;
  IsSpecial: boolean;
  ContextID: string;
  SystemControlName: string;
  CustomControlLabel: string;
  LogicRuleCount: number | null;
  CodingFormControlTypeCode: string;
  ErrorMessage: string;
  DefaultAction: string;
  HelpText: string;
  ControlLogicJSon: string;
  GroupControlName: string;
  FieldLogicAction: string;
  ControlValue: string;
  children: CodingFormItem[] | null;
  MetaDataType: string | null;
  MaxLength: number;
  MinValue: number | null;
  MaxValue: number | null;
  SplitLength: number;
  DataTypeLengthCode: string | null;
  AiPredictionClassification: string | null;
  AiPredictionScore: number | null;
  GroupDisplayType: string;
}

const CodingForm: React.FC<{
  panelId?: string;
  codingFormPinLabel?: CodingFormPinLabel;
  onToastShow?: (message: string, type: any) => void;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
}> = ({codingFormPinLabel, onToastShow, setIsDirty, panelId}) => {
  const docView = useSelector((state: RootState) => state.docView);
  const [
    isCompleteAllowedForDocumentOutsideBatch,
    setIsCompleteAllowedForDocumentOutsideBatch,
  ] = useSignalStore('isCompleteAllowedForDocumentOutsideBatch', true);
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const totalDocsCount = useSelector(
    (state: RootState) => state.docView.totalDocsCount
  );

  const docViewConfiguration = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );

  const {
    isCompleteWhenCodingStampApplied,
    isAllowCodingOutsideReviewerBatch,
    codingstamp: showCodingStamps,
    save: showSaveCodingFormButton,
    shouldSaveWhenSwitchedToDiffCF,
  } = docViewConfiguration;

  const saveCodingFormResponse = useSelector(
    (state: RootState) => state.docView.saveCodingFormResponse
  );

  const assignmentMode = useSelector(
    (state: RootState) => state.docView.assignmentMode
  );
  const sourceId = useSelector((state: RootState) => state.docView.sourceId);

  useEffect(() => {
    if (assignmentMode && !isAllowCodingOutsideReviewerBatch) {
      const checkOutsideBatch = async () => {
        let docsStatus;
        try {
          docsStatus = (await getDocIsCompleted({
            assignmentSource: '',
            sourceID: Number(sourceId),
            sourceTypeCode: 'ASSIGNMENT',
            selectedDocs: [`${activeDocumentObj?.documentId}`],
          })) as DocCompletedCheckResponse;
        } catch (error) {
          console.error(error);
        }
        if (docsStatus?.docNotInAssignment != '') {
          setIsCompleteAllowedForDocumentOutsideBatch(false);
        }
      };
      checkOutsideBatch();
    }
  }, [isAllowCodingOutsideReviewerBatch, assignmentMode, activeDocumentObj]);
  const isDocumentCompleted = useSelector(
    (state: RootState) => state.docView.isDocumentCompleted
  );

  const sourceTypeCode = useSelector(
    (state: RootState) => state.docView.sourceTypeCode
  );

  const assignmentSource = useSelector(
    (state: RootState) => state.docView.assignmentSource
  );

  const showCompletedDocs = useSelector(
    (state: RootState) => state.docView.showCompletedDocs
  );
  const showCompleteButtons =
    assignmentMode &&
    assignmentSource === 'REVIEWERDASHBOARD' &&
    sourceTypeCode === 'ASSIGNMENT' &&
    isCompleteAllowedForDocumentOutsideBatch;

  const activeDocumentId = Number(activeDocumentObj?.documentId);
  const prevDocIdRef = useRef<number | null>(null);

  // Check if current document is the last document
  const currentRowNumber = Number(activeDocumentObj?.rowNumber ?? 0);
  const isLastDocument = currentRowNumber >= totalDocsCount;

  const codeSameAsDocumentCodingForm = useSelector(
    (state: RootState) => state.docView.codeSameAsDocumentCodingForm
  );

  const codeSameAsDocs = useSelector(
    (state: RootState) => state.docView.codeSameAsDocs ?? []
  );

  const payload = {
    assignmentSource: docView.assignmentSource ?? '',
    sourceTypeCode: docView.sourceTypeCode ?? '',
    sourceID: docView.sourceId ?? 0,
  };
  const [selectedCodingForm, setSelectedCodingForm] =
    useSignalStore<ISelectItem | null>('selectedCodingForm', null);
  const [pinnedCodingForm, setPinnedCodingForm] =
    useSignalStore<ISelectItem | null>('pinnedCodingForm', null);
  const [defaultCodingForm, setDefaultCodingForm] =
    useSignalStore<ISelectItem | null>('defaultCodingForm', null);
  const [
    selectedCodingFormBeforeSwitching,
    setSelectedCodingFormBeforeSwitching,
  ] = useSignalStore<ISelectItem | null>(
    'selectedCodingFormBeforeSwitching',
    null
  );
  const [codingStamp, setCodingStamp] = useSignalStore<CodingStampItem[]>(
    'codingStamp',
    []
  );
  const [selectedStampId, setSelectedStampId] = useSignalStore<number | null>(
    'selectedStampId',
    null
  );
  const [codingConflicts, setCodingConflicts] = useSignalStore<string[]>(
    'codingConflicts',
    []
  );
  const [_isAddStamp, setIsAddStamp] = useSignalStore<boolean>(
    'isAddStamp',
    false
  );
  const [completeAfterUpdate, setCompleteAfterUpdate] = useSignalStore(
    'completeAfterUpdate',
    false
  );
  const [codingFormReady, setCodingFormReady] = useSignalStore(
    'codingFormReady',
    false
  );

  const [_isEditCodingFormOpen, setIsEditCodingFormOpen] =
    useSignalStore<boolean>('isEditCodingFormOpen', false);
  const [isStampEditActive, setIsStampEditActive] = useSignalStore<boolean>(
    'isStampEditActive',
    false
  );
  const [defaultCodingFormId, setDefaultCodingFormId] = useSignalStore<
    number | null
  >('defaultCodingFormId', null);
  const [documentCodingForm, setDocumentCodingForm] = useSignalStore<
    CodingFormItem[]
  >('documentCodingForm', []);
  const [updatedDocumentCodingForm, setUpdatedDocumentCodingForm] =
    useSignalStore<CodingFormItem[]>('updatedDocumentCodingForm', []);
  const selectedSourceId = useSelector(
    (state: RootState) => state.docView.sourceId
  );
  const [isSaving, setIsSaving] = useSignalStore<boolean>('isSaving', false);
  const [messageType, setMessageType] = useSignalStore<ToastType | null>(
    'messageType',
    null
  );

  const payloadCodingForm = {
    codingFormId: Number(selectedCodingForm?.id ?? 0),
    documentId: activeDocumentId,
    assignmentSource: '',
    sourceTypeCode: docView.sourceTypeCode ?? '',
    sourceID: selectedSourceId ? selectedSourceId : 0,
  };
  const {data: codingFormList, refetch: refetchCodingFormList} =
    useDocumentCodingFormList(payload);
  const {
    data: codingForm,
    isLoading: isCodingFormLoading,
    refetch: refetchCodingForm,
  } = useDocumentCodingForm(
    payloadCodingForm,
    Boolean(activeDocumentId && selectedCodingForm?.id)
  );
  const {mutate: mutatePinCodingForm, data: pinCodingFormData} =
    usePinCodingForm();
  const {mutate: mutateCodeSameAsPrevious} = useCodeSameAsPrevious();
  const {mutate: getEditCodingDetails, data: codingStampDetails} =
    useGetEditCodingDetails();

  const [codingFormOnSwitchForSave, setCodingFormOnSwitchForSave] =
    useSignalStore<ISelectItem | null>('codingFormOnSwitchForSave', null);
  const [isUpdated, setIsUpdated] = useSignalStore('isUpdated', false);
  const setCodingFormUpdated = (value: boolean) => {
    setIsDirty?.(value);
    setIsUpdated(value);
  };
  const dispatch = useDispatch();

  const nextIdRef = useRef<number>(0);
  const dynamicFormRef = useRef<DynamicFormFieldsHandle>(null);

  const nextDoc = useSelector((state: RootState) => state.docView.nextDoc);

  // Helper function to fetch next row data
  const fetchRowData = async (
    start: number,
    pageSize: number
  ): Promise<IDocViewDocListRowData[]> => {
    const payload = prepareMiniDocDataRequest(
      start,
      pageSize - 1,
      docView.documentListMasterId,
      docViewConfiguration,
      sourceTypeCode
    );

    let response: MiniDocListResponse;
    let rowsData: IDocViewDocListRowData[] = [];

    try {
      response = (await getMiniDocListGridData(payload)) as MiniDocListResponse;
      rowsData = JSON.parse(response.serverData) as IDocViewDocListRowData[];
    } catch {
      // console.error('Error fetching next document:', error);
    }

    rowsData =
      rowsData?.map((row) => ({
        ...row,
        IsChecked: false,
        IsCodeSame: false,
        IsSelected: false,
        IsCompleted: Boolean(Number(row.IsCompleted)),
      })) ?? [];

    return rowsData;
  };

  const handleNavigateToNextDocument = async (isComplete = false) => {
    const currentRowNumber = Number(activeDocumentObj?.rowNumber ?? 0);

    if (currentRowNumber < totalDocsCount) {
      const nextRowNumber =
        showCompletedDocs || !isComplete
          ? currentRowNumber + 1
          : currentRowNumber;

      if (nextDoc) {
        // Update row number for completed docs
        const _nextDoc = {
          ...nextDoc,
          rowNumber: Number(nextDoc.rowNumber ?? nextRowNumber),
        }; //nextDoc is read-only

        dispatch(setActiveDoc(_nextDoc));
        const nextDocIsCompleted = Boolean(Number(_nextDoc.isCompleted));
        dispatch(setIsDocumentCompleted(nextDocIsCompleted));
      } else {
        const rows = await fetchRowData(nextRowNumber, 1);
        if (rows.length > 0) {
          const nextRow = rows[0];
          const nextDocument = {
            docId: nextRow.DocID,
            documentId: nextRow.MiniDocListDocID,
            fileName: nextRow.FileName,
            rowNumber: nextRowNumber,
            isCompleted: nextRow.IsCompleted,
            fileTypeCode: nextRow.FileTypeCode,
            miniMediaLength: nextRow.MiniMediaLength,
            miniAudioTrimmedLength: nextRow.MiniAudioTrimmedLength,
            miniAudioDocDateTimeOnly: nextRow.MiniAudioDocDateTimeOnly,
            docExt: nextRow.MiniDocListDocExt as string,
            documentGUID: nextRow.DocumentGUID,
            ingestionGUID: nextRow.IngestionGUID,
            __InternalExcelProtectedSheets:
              nextRow.__InternalExcelProtectedSheets,
            __InternalExcelProtectedWorkbook:
              nextRow.__InternalExcelProtectedWorkbook,
            __InternalHiddenProperties: nextRow.__InternalHiddenProperties,
            __InternalHiddenPropertiesText:
              nextRow.__InternalHiddenPropertiesText,
          };
          dispatch(setActiveDoc(nextDocument));
          const nextDocIsCompleted = Boolean(Number(nextRow.IsCompleted));
          dispatch(setIsDocumentCompleted(nextDocIsCompleted));
        }
        if (pinnedCodingForm) {
          setSelectedCodingForm(pinnedCodingForm);
        } else {
          setSelectedCodingForm(defaultCodingForm);
        }
      }
    }
  };
  useEffect(() => {
    if (activeDocumentObj) {
      const isCompleted = Boolean(Number(activeDocumentObj.isCompleted));

      dispatch(setIsDocumentCompleted(isCompleted));
    }
  }, [activeDocumentObj?.documentId, activeDocumentObj?.isCompleted, dispatch]);
  useEffect(() => {
    storeSetIsPinCodingFormSelected(
      codingFormList?.some(
        (item) => item.codingFormId === selectedCodingForm?.id && item.isPin
      ) ?? false
    );
  }, [selectedCodingForm, codingFormList]);

  useEffect(() => {
    if (
      codingFormPinLabel === CodingFormPinLabel.pin ||
      codingFormPinLabel === CodingFormPinLabel.pinAgain
    ) {
      const selectedPin = codingFormList?.find(
        (item) => item.isPin
      )?.codingFormId;
      const payload = {
        codingFormId: selectedPin
          ? Number(selectedPin)
          : Number(selectedCodingForm?.id),
        assignmentID: sourceId,
        isPin: !selectedPin,
      };
      mutatePinCodingForm(payload);
      if (selectedPin) {
        const payload = {
          codingFormId: Number(selectedCodingForm?.id),
          assignmentID: sourceId,
          isPin: true,
        };
        mutatePinCodingForm(payload);
      }
    } else if (codingFormPinLabel === CodingFormPinLabel.unpin) {
      const payload = {
        codingFormId: Number(selectedCodingForm?.id),
        assignmentID: sourceId,
        isPin: false,
      };
      mutatePinCodingForm(payload);
    }
  }, [codingFormPinLabel]);

  useEffect(() => {
    if (pinCodingFormData?.saveUnsaved) {
      refetchCodingFormList();
    }
  }, [pinCodingFormData]);

  function detectCodingConflicts(codingFormData: CodingFormItems[]): string[] {
    const radioGroups = new Map<
      string,
      {
        label: string;
        checkedTags: string[];
      }
    >();

    // First pass: collect all RADIOGROUP definitions
    for (const control of codingFormData) {
      if (control.CodingFormControlTypeCode === 'RADIOGROUP') {
        radioGroups.set(control.SystemControlName, {
          label: control.CustomControlLabel,
          checkedTags: [],
        });
      }
    }

    // Second pass: collect all checked RADIOs, regardless of position
    for (const control of codingFormData) {
      if (
        control.CodingFormControlTypeCode === 'RADIO' &&
        control.ControlValue === 'True' &&
        control.GroupControlName
      ) {
        const group = radioGroups.get(control.GroupControlName);
        if (group) {
          group.checkedTags.push(control.CustomControlLabel);
        }
      }
    }

    // Build conflict messages for groups with more than one checked option
    const conflicts: string[] = [];
    for (const {label, checkedTags} of radioGroups.values()) {
      if (checkedTags.length > 1) {
        conflicts.push(
          `${label} ${t(
            'docView.headers.codingForm.codingConflictStatement2'
          )} ${checkedTags.join(', ')}.`
        );
      }
    }

    return conflicts;
  }
  useEffect(() => {
    if (codingForm) {
      const parsedCodingForm = codingForm?.data
        ? JSON.parse(codingForm?.data)
        : [];
      const parsedStamp: CodingStampItem[] = codingForm?.codingStamp
        ? JSON.parse(codingForm.codingStamp)
        : [];
      setIsStampEditActive(
        (prev) =>
          prev &&
          parsedStamp.some(
            (stamp) =>
              stamp.CodingFormStampColorName &&
              stamp.CodingFormUserStampID !== 0
          )
      );

      setCodingStamp(parsedStamp);
      const conflicts = detectCodingConflicts(parsedCodingForm);
      setCodingConflicts(conflicts);
      nextIdRef.current = 0;
      if (parsedCodingForm) {
        const newItems = parsedCodingForm?.map(
          (control: any, index: number) => {
            let itemId = Number(control.contextID);
            if (control.systemControlName) {
              const parts = control.systemControlName.split('_');
              if (parts.length > 1) {
                const parsedId = Number(parts[1]);
                if (!isNaN(parsedId)) {
                  itemId = parsedId;
                }
              }
            }

            return {
              id: control.id,
              itemId,
              cfItemID: String(index),
              sequence: control.Sequence,
              type: control.ContextType || '',
              specialFlag: control.IsSpecial || false,
              value: control.ContextID || '',
              systemLabel: control.SystemControlName || '',
              friendlyLabel: control.CustomControlLabel || '',
              logicRuleCount: String(control.LogicRuleCount || '0'),
              tagType: control.CodingFormControlTypeCode || '',
              errorMessage: control.ErrorMessage || '',
              defaultAction: control.DefaultAction || '',
              helpText: control.HelpText || '',
              fieldLogic: JSON.parse(control.ControlLogicJSon || '[]'),
              fieldLogicJson: control.ControlLogicJSon || '',
              fieldLogicAction: control.FieldLogicAction || '',
              fieldLogicObjectLabel: `${control.CustomControlLabel} (${control.SystemControlName})`,
              groupAssociation: control.GroupControlName
                ? {
                    value: control.GroupControlName,
                    label: control.GroupControlName,
                  }
                : null,
              metaDataType: control.MetaDataType || '',
              maxLength: control.MaxLength
                ? Number(control.MaxLength)
                : undefined,
              minValue: control.MinValue || '',
              maxValue: control.MaxValue || '',
              splitLength: control.SplitLength
                ? Number(control.SplitLength)
                : undefined,
              dataTypeLengthCode: control.DataTypeLengthCode || '',
              isCollapsed: true,
              controlValue: control.ControlValue || '',
              displayType: control.GroupDisplayType || 'DEFAULT',
            };
          }
        );
        if (docView.isCodeSameAsPrevious) {
          setDocumentCodingForm(codeSameAsDocumentCodingForm);
          setUpdatedDocumentCodingForm(codeSameAsDocumentCodingForm);
          dispatch(setIsCodeSameAsPrevious(false));
          return;
        }
        setDocumentCodingForm(newItems);
        setUpdatedDocumentCodingForm(newItems);
      }
    }
  }, [JSON.stringify(codingForm), docView.isCodeSameAsPrevious]);

  useEffect(() => {
    if (codingStampDetails) {
      const conflicts = detectCodingConflicts(codingStampDetails);
      setCodingConflicts(conflicts);
      nextIdRef.current = 0;
      if (codingStampDetails) {
        const newItems = codingStampDetails?.map(
          (control: any, index: number) => {
            let itemId = Number(control.contextID);
            if (control.systemControlName) {
              const parts = control.systemControlName.split('_');
              if (parts.length > 1) {
                const parsedId = Number(parts[1]);
                if (!isNaN(parsedId)) {
                  itemId = parsedId;
                }
              }
            }

            return {
              id: control.id,
              itemId,
              cfItemID: String(index),
              sequence: control.sequence,
              type: control.contextType || '',
              specialFlag: control.isSpecial || false,
              value: control.contextID || '',
              systemLabel: control.systemControlName || '',
              friendlyLabel: control.customControlLabel || '',
              logicRuleCount: String(control.logicRuleCount || '0'),
              tagType: control.codingFormControlTypeCode || '',
              errorMessage: control.errorMessage || '',
              defaultAction: control.defaultAction || '',
              helpText: control.helpText || '',
              fieldLogic: JSON.parse(control.controlLogicJSon || '[]'),
              fieldLogicJson: control.controlLogicJSon || '',
              fieldLogicAction: control.fieldLogicAction || '',
              fieldLogicObjectLabel: `${control.customControlLabel} (${control.systemControlName})`,
              groupAssociation: control.groupControlName
                ? {
                    value: control.groupControlName,
                    label: control.groupControlName,
                  }
                : null,
              metaDataType: control.metaDataType || '',
              maxLength: control.maxLength
                ? Number(control.maxLength)
                : undefined,
              minValue: control.minValue || '',
              maxValue: control.maxValue || '',
              splitLength: control.splitLength
                ? Number(control.splitLength)
                : undefined,
              dataTypeLengthCode: control.dataTypeLengthCode || '',
              isCollapsed: true,
              controlValue: control.controlValue || '',
              displayType: control.groupDisplayType || 'DEFAULT',
            };
          }
        );

        setDocumentCodingForm(newItems);
        setUpdatedDocumentCodingForm(newItems);
        if (!isCompleteWhenCodingStampApplied) {
          setCodingFormUpdated(true);
        }
      }
    }
  }, [codingStampDetails, isCompleteWhenCodingStampApplied]);

  useEffect(() => {
    if (
      completeAfterUpdate &&
      documentCodingForm === updatedDocumentCodingForm &&
      codingFormReady
    ) {
      // When Coding Stamp is selected , updatedCodingForm and documentCodingForm variables are updated
      // Once the Dynamic form shows Stamp Field Values , then handle complete
      // completeAfterUpdate variable is updated when user clicks on Stamp and has Required permissions
      handleComplete();
    }
  }, [
    updatedDocumentCodingForm,
    completeAfterUpdate,
    documentCodingForm,
    codingFormReady,
  ]);

  useEffect(() => {
    const pinItem = codingFormList?.find((item) => item.isPin);
    const defaultForm = codingFormList?.find(
      (item: IDocumentViewerCodingFormListResponse) => item.isDefault === true
    );

    const isDocChanged = prevDocIdRef.current !== activeDocumentId;

    if (pinItem) {
      const pinned = {
        id: pinItem.codingFormId,
        name: pinItem.codingFormName,
        isPin: pinItem.isPin,
      };

      setSelectedCodingForm(pinned);
      setPinnedCodingForm(pinned);
    } else {
      setPinnedCodingForm(null);

      const defaultCF = {
        id: defaultForm?.codingFormId,
        name: defaultForm?.codingFormName,
        isDefault: defaultForm?.isDefault,
        isPin: defaultForm?.isPin,
      };

      if (isDocChanged) {
        setSelectedCodingForm(defaultCF);
      } else {
        const selectedCF = {
          id: selectedCodingForm?.id,
          name: selectedCodingForm?.name,
          isDefault: selectedCodingForm?.isDefault,
          isPin: false,
        };
        setSelectedCodingForm(selectedCF);
      }

      setDefaultCodingForm(defaultCF);
    }

    // Mark the doc as handled only once the list has loaded; otherwise on page
    // refresh (activeDoc restored from persisted store) the default coding
    // form is never selected and the coding form API never fires
    if (codingFormList) {
      prevDocIdRef.current = activeDocumentId;
    }

    setDefaultCodingFormId(defaultForm?.codingFormId ?? null);
    storeCodingFormList(codingFormList ?? []);
  }, [codingFormList, activeDocumentId]);

  const handleSelectedStampCode = (selectedStampCode: string | null) => {
    if (selectedStampCode) {
      getEditCodingDetails({
        sourceID: sourceId ?? 0,
        CodingFormId: Number(selectedCodingForm?.id ?? 0),
        DocumentId: activeDocumentId,
        Code: selectedStampCode,
        SourceTypeCode: 'DOCUMENT',
      });
    }
  };

  const handleCodeSameAsPrevious = () => {
    const payload = {
      currentDocId: activeDocumentId.toString() ?? '',
      sourceTypeCode: docView.sourceTypeCode || 'DOCUMENT',
      sourceID: docViewConfiguration.assignmentId ?? 0,
      xmlCompletedDocumentEditedCodingForm:
        saveCodingFormResponse?.xmlCompletedDocumentEditedCodingForm ?? '',
      userCodingFormID: Number(selectedCodingForm?.id ?? 0),
      docsForCodeSame: [],
    };
    mutateCodeSameAsPrevious(payload, {
      onSuccess: (data) => {
        storeSourceTypeCode(data?.sourceTypeCode ?? '');
        if (nextDoc) {
          setActiveDocument(nextDoc);
          dispatch(setIsCodeSameAsPrevious(true));

          onToastShow?.(
            t('docView.headers.codingForm.toast.codedSameAsPreviousSuccess'),
            ToastType.Success
          );
        } else {
          const currentRowNumber = Number(activeDocumentObj?.rowNumber ?? 0);
          const startNumber =
            currentRowNumber < totalDocsCount
              ? currentRowNumber + 1
              : currentRowNumber;
          // Fetch the next row data
          fetchRowData(startNumber, 1).then((rows) => {
            if (rows.length > 0) {
              const nextRow = rows[0];
              // Create active document object for the next document
              const nextDocument = getNextDocument(nextRow);

              setActiveDocument(nextDocument);
              dispatch(setIsCodeSameAsPrevious(true));

              onToastShow?.(
                t(
                  'docView.headers.codingForm.toast.codedSameAsPreviousSuccess'
                ),
                ToastType.Success
              );
            }
          });
        }
        setCodingFormUpdated(false);
      },
      onError: (error) => {
        onToastShow?.(error?.message, ToastType.Error);
      },
    });
  };

  const buildSaveCodingFormPayload = (
    isComplete: number = 0
  ): ISaveCodingFormRequest => {
    const codedObject = updatedDocumentCodingForm
      .filter((field) => {
        // For COMMENT fields, include them even if empty (to allow clearing)
        if (
          field.type === 'COMMENT' ||
          field.tagType === 'COMMENT' ||
          field.tagType === 'TEXTAREA'
        ) {
          return true; // Always include comments, even if blank(as behaves in sightline)
        }
        // For other fields, only include if they have a value
        const hasValue =
          field.controlValue !== null &&
          field.controlValue !== undefined &&
          field.controlValue !== '' &&
          String(field.controlValue).trim() !== '';
        return hasValue;
      })
      .map((field) => {
        let typeValue = field.type || '';
        if (field.type === 'TAG' && field.groupAssociation?.value) {
          typeValue = String(field.groupAssociation.value);
        } else if (field.type === 'RADIOGROUP' || field.type === 'CHECKGROUP') {
          typeValue = field.systemLabel || field.type;
        } else if (field.type === 'COMMENT') {
          typeValue = field.type;
        }
        return {
          id: field.value ? String(field.value) : String(field.id),
          type: typeValue,
          value: String(field.controlValue || ''),
          fieldName: field.friendlyLabel || '',
          controlType: field.tagType || field.type || '',
        };
      });

    const docsForCodeSame = Array.isArray(codeSameAsDocs)
      ? codeSameAsDocs
          .filter((doc) => doc.documentId !== String(activeDocumentId))
          .map((doc) => doc.documentId)
      : [];

    return {
      docsForCodeSame: docsForCodeSame,
      docViewSaveComplete: {
        codedObject,
        isComplete: isComplete,
        currentDocID: activeDocumentId,
        sourceTypeCode: docView.sourceTypeCode ?? '',
        sourceID: sourceId ?? 0,
        stampColorCode: selectedStamp?.CodingFormStampColorCode ?? '',
        codingFormID: Number(selectedCodingForm?.id ?? 0),
        ingestionGuid: activeDocumentObj?.ingestionGUID ?? '',
        documentGuid: activeDocumentObj?.documentGUID ?? '',
      },
      sourceID: sourceId ?? 0,
      userCodingFormID: Number(selectedCodingForm?.id ?? 0),
      sourceTypeCode: docView.sourceTypeCode ?? '',
      isDefault: 0, // defaultCodingFormId === selectedCodingForm?.id ? 1 : 0,
    };
  };

  const handleSave = async () => {
    const isValid = dynamicFormRef.current?.handleValidate();
    if (!isValid) {
      return 'Error';
    }
    setIsSaving(true);
    try {
      const payload = buildSaveCodingFormPayload(0);
      const response = await saveCodingFormData(payload);
      const savedDocs = Array.isArray(codeSameAsDocs)
        ? [
            activeDocumentObj as MiniDocRowSelection,
            ...codeSameAsDocs.filter(
              (doc) => doc.documentId !== String(activeDocumentId)
            ),
          ]
        : [activeDocumentObj as MiniDocRowSelection];

      dispatch(removeCodeSameAsDocs(savedDocs));
      dispatch(setSaveCodingFormResponse(response));
      dispatch(setCodeSameAsDocumentCodingForm(updatedDocumentCodingForm));
      setMessageType(null);
      setCodingFormUpdated(false);

      onToastShow?.(
        t('docView.headers.codingForm.toast.savedSuccessfully'),
        ToastType.Success
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getNextDocument = (nextRow: any) => {
    return {
      docId: nextRow.DocID,
      documentId: nextRow.MiniDocListDocID,
      fileName: nextRow.FileName,
      rowNumber: currentRowNumber + 1,
      isCompleted: nextRow.IsCompleted,
      fileTypeCode: nextRow.FileTypeCode,
      miniMediaLength: nextRow.MiniMediaLength,
      miniAudioTrimmedLength: nextRow.MiniAudioTrimmedLength,
      miniAudioDocDateTimeOnly: nextRow.MiniAudioDocDateTimeOnly,
      docExt: nextRow.MiniDocListDocExt as string,
      documentGUID: nextRow.DocumentGUID,
      ingestionGUID: nextRow.IngestionGUID,
      __InternalExcelProtectedSheets: nextRow.__InternalExcelProtectedSheets,
      __InternalExcelProtectedWorkbook:
        nextRow.__InternalExcelProtectedWorkbook,
      __InternalHiddenProperties: nextRow.__InternalHiddenProperties,
      __InternalHiddenPropertiesText: nextRow.__InternalHiddenPropertiesText,
      ActualParameters: nextRow?.ActualParameters ?? '',
      ConceptParameters: nextRow?.ConceptParameters ?? '',
      MinConceptScore: nextRow?.MinConceptScore,
    };
  };

  const handleSaveAndNext = async () => {
    const isValid = dynamicFormRef.current?.handleValidate();
    if (!isValid || messageType === ToastType.Error) {
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildSaveCodingFormPayload();
      const response = await saveCodingFormData(payload);
      dispatch(setSaveCodingFormResponse(response));
      dispatch(setCodeSameAsDocumentCodingForm(updatedDocumentCodingForm));
      setMessageType(null);

      const savedDocs = Array.isArray(codeSameAsDocs)
        ? [
            activeDocumentObj as MiniDocRowSelection,
            ...codeSameAsDocs.filter(
              (doc) => doc.documentId !== String(activeDocumentId)
            ),
          ]
        : [activeDocumentObj as MiniDocRowSelection];

      dispatch(removeCodeSameAsDocs(savedDocs));

      await handleNavigateToNextDocument();
      setCodingFormUpdated(false);
      onToastShow?.(
        t('docView.headers.codingForm.toast.savedSuccessfully'),
        ToastType.Success
      );
    } catch (error) {
      console.error('Failed to save and proceed to next document', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = async () => {
    const isValid = dynamicFormRef.current?.handleValidate();
    if (!isValid) {
      setCompleteAfterUpdate(false);
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildSaveCodingFormPayload(1);
      const response = await saveCodingFormData(payload);
      setMessageType(null);

      const completedDocument: MiniDocRowSelection = {
        documentId: activeDocumentObj?.documentId ?? '',
        rowNumber: activeDocumentObj?.rowNumber ?? 0,
        isCompleted: true,
        docId: activeDocumentObj?.docId ?? '',
        fileName: activeDocumentObj?.fileName ?? '',
        fileTypeCode: activeDocumentObj?.fileTypeCode,
        miniMediaLength: activeDocumentObj?.miniMediaLength,
        miniAudioTrimmedLength: activeDocumentObj?.miniAudioTrimmedLength,
        miniAudioDocDateTimeOnly: activeDocumentObj?.miniAudioDocDateTimeOnly,
        docExt: activeDocumentObj?.docExt,
        documentGUID: activeDocumentObj?.documentGUID,
        ingestionGUID: activeDocumentObj?.ingestionGUID,
        __InternalExcelProtectedSheets:
          activeDocumentObj?.__InternalExcelProtectedSheets,
        __InternalExcelProtectedWorkbook:
          activeDocumentObj?.__InternalExcelProtectedWorkbook,
        __InternalHiddenProperties:
          activeDocumentObj?.__InternalHiddenProperties,
        __InternalHiddenPropertiesText:
          activeDocumentObj?.__InternalHiddenPropertiesText,
      };

      const codeSameAsCompletedDocs = Array.isArray(codeSameAsDocs)
        ? codeSameAsDocs
            .filter((doc) => doc.documentId !== String(activeDocumentId))
            .map((doc) => ({
              ...doc,
              isCompleted: true,
            }))
        : [];

      const allCompletedDocuments = [
        completedDocument,
        ...codeSameAsCompletedDocs,
      ];

      setCompletedDocuments(allCompletedDocuments);

      dispatch(setSaveCodingFormResponse(response));

      if (updatedDocumentCodingForm) {
        dispatch(setCodeSameAsDocumentCodingForm(updatedDocumentCodingForm));
      }

      if (codeSameAsCompletedDocs.length > 0) {
        dispatch(
          removeCodeSameAsDocs([completedDocument, ...codeSameAsCompletedDocs])
        );
      } else {
        dispatch(clearCodeSameAsDocsId());
      }

      dispatch(setIsDocumentCompleted(true));
      dispatch(incrementDocAnalyticsRefreshKey());

      if (assignmentMode) {
        dispatch(triggerAssignmentProgressRefresh());
      }

      onToastShow?.(
        t('docView.headers.codingForm.toast.documentCompleted'),
        ToastType.Success
      );

      await handleNavigateToNextDocument(true);
      setCodingFormUpdated(false);
    } catch (e) {
      console.log(e);
    } finally {
      setCompleteAfterUpdate(false);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (messageType === ToastType.Error) {
      onToastShow?.(
        t('docView.headers.codingForm.toast.codingFailed'),
        ToastType.Error
      );
      setMessageType(null);
    }
  }, [messageType]);

  const handleUncomplete = async () => {
    setIsSaving(true);
    try {
      const payload = buildSaveCodingFormPayload(2);
      await saveCodingFormData(payload);
      setMessageType(null);

      const uncompletedDocument: MiniDocRowSelection = {
        documentId: activeDocumentObj?.documentId ?? '',
        rowNumber: activeDocumentObj?.rowNumber ?? 0,
        isCompleted: false,
        docId: activeDocumentObj?.docId ?? '',
        fileName: activeDocumentObj?.fileName ?? '',
      };
      removeCompletedDocuments([uncompletedDocument]);

      dispatch(setIsDocumentCompleted(false));
      console.log('Uncomplete: Set isDocumentCompleted to false');
      dispatch(incrementDocAnalyticsRefreshKey());

      if (assignmentMode) {
        dispatch(triggerAssignmentProgressRefresh());
      }

      onToastShow?.(
        t('docView.headers.codingForm.toast.documentUncompleted'),
        ToastType.Success
      );

      refetchCodingForm();
    } catch (error) {
      console.error('Uncomplete: Failed to uncomplete document', error);
    } finally {
      setIsSaving(false);
    }
  };
  const selectedStamp = codingStamp?.find(
    (stamp) => stamp.CodingFormUserStampID === selectedStampId
  );

  return isCodingFormLoading ? (
    <ArrLoadingSpinner />
  ) : (
    <Box className="doc-coding-form-container">
      <ArrModal
        showModal={!!codingFormOnSwitchForSave}
        title={t('docView.headers.codingForm.loseChanges')}
        showClose
        onClose={() => {
          setSelectedCodingForm(selectedCodingFormBeforeSwitching);
          setCodingFormOnSwitchForSave(null);
        }}
        dataTestId="coding-form-modal"
        buttons={[
          {
            id: '1',
            label: t('docView.headers.codingForm.loseChangesCancel'),
            onClick: () => {
              setSelectedCodingForm(selectedCodingFormBeforeSwitching);
              setCodingFormOnSwitchForSave(null);
            },
            variant: 'secondary',
            dataTestId: 'cfm-cancel-btn',
          },
          {
            id: '2',
            label: t('docView.headers.codingForm.loseChangesOK'),

            onClick: () => {
              setCodingFormOnSwitchForSave(null);
              setSelectedCodingForm(codingFormOnSwitchForSave);
              setCodingFormUpdated(false);
            },
            variant: 'primary',
            dataTestId: 'cfm-ok-btn',
          },
        ]}
        description={t('docView.headers.codingForm.loseChangesDescription')}
      ></ArrModal>
      {showCodingStamps && (
        <Box
          className="icon-action-stamp-container"
          data-tab-group={panelId}
          data-tab-id="code-action-stamp-buttons"
        >
          <ArrIconButton
            shape="none"
            size="small"
            icon={<PlusIcon />}
            onClick={() => setIsAddStamp(true)}
            ariaLabel="add-coding-stamp"
            tooltipContent="Add Coding Stamp"
            tooltipFocusableWrapper
            testId="add-coding-stamp"
            data-tab-group={panelId}
            data-tab-id="add-coding-stamp"
          />

          <ArrIconButton
            className={`edit-btn ${isStampEditActive ? 'active' : ''}`}
            shape="none"
            size="small"
            icon={<EditOutlinedIcon />}
            ariaLabel="edit-coding-stamp"
            tooltipContent={`${!isStampEditActive ? 'Edit Coding Stamp' : ''}`}
            testId="edit-coding-stamp"
            disabled={
              !codingStamp.some(
                (stamp) =>
                  stamp.CodingFormStampColorName &&
                  stamp.CodingFormUserStampID !== 0
              )
            }
            tooltipFocusableWrapper
            onClick={() => setIsStampEditActive(!isStampEditActive)}
            data-tab-group={panelId}
            data-tab-id="edit-coding-stamp"
          />

          <Divider className="stamp-divider" />
          <Box
            className={`stamps-container ${isStampEditActive ? 'edit-active' : ''}`}
          >
            {codingStamp?.map((stamp, id) => (
              <div
                key={stamp.CodingFormStampColorLabel}
                className={`stamp-icon-wrapper ${
                  selectedStampId === stamp.CodingFormUserStampID &&
                  stamp.CodingFormUserStampID !== 0
                    ? 'stamp-icon-active'
                    : ''
                }`}
                style={
                  stamp.CodingFormStampColorName &&
                  stamp.CodingFormUserStampID !== 0
                    ? {
                        borderBottom: `3px solid #${stamp.CodingFormStampColorRGB}`,
                        paddingBottom: '4px',
                      }
                    : {}
                }
                data-testid={`coding-form-icon-wrapper-${id}`}
              >
                <ArrIconButton
                  shape="none"
                  size="small"
                  icon={
                    <TagTreeIcon
                      style={{color: `#${stamp.CodingFormStampColorRGB}`}}
                    />
                  }
                  disabled={
                    (!stamp.CodingFormStampColorName ||
                      stamp.CodingFormUserStampID === 0) &&
                    isStampEditActive
                  }
                  ariaLabel="edit-stamp"
                  tooltipContent={stamp?.CodingFormStampColorName}
                  testId={`use-stamp-${stamp.CodingFormStampColorRGB}`}
                  onClick={() => {
                    if (isDocumentCompleted) return;
                    if (
                      stamp.CodingFormStampColorName &&
                      stamp.CodingFormUserStampID !== 0
                    ) {
                      setSelectedStampId(stamp.CodingFormUserStampID);
                      if (isStampEditActive) {
                        setIsEditCodingFormOpen(true);
                        return;
                      }
                      handleSelectedStampCode(stamp.CodingFormStampColorCode);
                      if (
                        isCompleteWhenCodingStampApplied &&
                        isCompleteAllowedForDocumentOutsideBatch
                      ) {
                        setCodingFormReady(false);
                        setCompleteAfterUpdate(true);
                      }
                    }
                  }}
                />
              </div>
            ))}
          </Box>
        </Box>
      )}

      <Box className="action-container">
        {/* Code Same as Previous - only show when NOT in reviewer dashboard assignment mode */}
        {defaultCodingFormId === selectedCodingForm?.id &&
          !showCompleteButtons &&
          isCompleteAllowedForDocumentOutsideBatch && (
            <ArrButton
              variant="text"
              size="small"
              onClick={handleCodeSameAsPrevious}
              dataTestId="code-same-as-previous-button"
              className="code-same-as-previous-button"
              name={t('docView.headers.codingForm.codeSameAsPrevious')}
              id={t('docView.headers.codingForm.codeSameAsPrevious')}
              disabled={
                isStampEditActive ||
                isSaving ||
                (saveCodingFormResponse?.jsonData <= 0 &&
                  saveCodingFormResponse?.xmlCompletedDocumentEditedCodingForm ===
                    '')
              }
            >
              {t('docView.headers.codingForm.codeSameAsPrevious')}
            </ArrButton>
          )}

        {/* Code Same as Previous for Reviewer Dashboard Assignment Mode */}
        {showCompleteButtons && (
          <ArrButton
            variant="text"
            size="small"
            onClick={handleCodeSameAsPrevious}
            dataTestId="code-same-as-previous-assignment-button"
            className="code-same-as-previous-button"
            name={t('docView.headers.codingForm.codeSameAsPrevious')}
            id={t('docView.headers.codingForm.codeSameAsPrevious')}
            disabled={
              (defaultCodingFormId === selectedCodingForm?.id &&
                isDocumentCompleted) ||
              (saveCodingFormResponse?.jsonData <= 0 &&
                saveCodingFormResponse?.xmlCompletedDocumentEditedCodingForm ===
                  '')
            }
          >
            {t('docView.headers.codingForm.codeSameAsPrevious')}
          </ArrButton>
        )}

        {/* Spacer to push right buttons to the right */}
        <Box sx={{flexGrow: 1}} />

        {/* Right-aligned buttons */}
        <Box className="right-action" sx={{display: 'flex', gap: '8px'}}>
          {showCompleteButtons ? (
            <>
              {showSaveCodingFormButton && assignmentMode && (
                <ArrButton
                  variant="text"
                  size="small"
                  onClick={handleSave}
                  dataTestId="cf-save-button"
                  className="save-button"
                  name={t('common.save')}
                  id={t('common.save')}
                  disabled={isSaving}
                >
                  {t('common.save')}
                </ArrButton>
              )}
              {isDocumentCompleted ? (
                <ArrButton
                  variant="primary"
                  size="small"
                  onClick={handleUncomplete}
                  dataTestId="uncomplete-button"
                  className="uncomplete-button"
                  name={t('docView.headers.codingForm.uncomplete')}
                  id={t('docView.headers.codingForm.uncomplete')}
                  disabled={isSaving}
                >
                  {t('docView.headers.codingForm.uncomplete')}
                </ArrButton>
              ) : (
                <ArrButton
                  variant="primary"
                  size="small"
                  onClick={handleComplete}
                  dataTestId="complete-button"
                  className="complete-button"
                  name={t('docView.headers.codingForm.complete')}
                  id={t('docView.headers.codingForm.complete')}
                  disabled={
                    isSaving ||
                    isCodingFormLoading ||
                    codingForm?.cannotComplete
                  }
                >
                  {t('docView.headers.codingForm.complete')}
                </ArrButton>
              )}
            </>
          ) : (
            <>
              <ArrButton
                variant="text"
                size="small"
                onClick={handleSaveAndNext}
                dataTestId="cf-save-and-next-button"
                className="save-and-next-button"
                name={t('docView.headers.codingForm.saveAndNext')}
                id={t('docView.headers.codingForm.saveAndNext')}
                disabled={isStampEditActive || isSaving || isLastDocument}
              >
                {t('docView.headers.codingForm.saveAndNext')}
              </ArrButton>

              <ArrButton
                variant="primary"
                size="small"
                onClick={handleSave}
                dataTestId="save-button"
                className="save-button"
                name={t('common.save')}
                id={t('common.save')}
                disabled={isStampEditActive || isSaving}
              >
                {t('common.save')}
              </ArrButton>
            </>
          )}
        </Box>
      </Box>

      {codingConflicts.length > 0 && (
        <ArrBanner
          type="persistent"
          variant="error"
          startIcon={<ErrorOutlineIcon />}
          bannerContent={
            <Box>
              <Box>
                {t('docView.headers.codingForm.codingConflictStatement')}
              </Box>
              {codingConflicts.map((conflict, index) => (
                <Box key={index}>{conflict}</Box>
              ))}
            </Box>
          }
          className="coding-conflicts-banner"
          dataTestId="coding-form-banner"
        />
      )}

      <Box className="coding-dropdown-container">
        <ArrSingleSelectDropdown
          sizeVariant="small"
          dropdownLabel={t('userManagement.assignUsers.role')}
          dropdownItems={
            codingFormList?.map((item) => ({
              id: item.codingFormId ?? 0,
              name: item.codingFormName,
              isPin: item.isPin,
            })) ?? []
          }
          selectedValues={selectedCodingForm}
          validateBeforeSettingValue={async (values: ISelectItem | null) => {
            if (
              isUpdated &&
              dynamicFormRef.current?.handleValidate &&
              shouldSaveWhenSwitchedToDiffCF &&
              assignmentMode
            ) {
              setMessageType(null);
              const isValid = await handleSave();
              return isValid !== 'Error';
            }
            selectedCodingForm !== null &&
              setSelectedCodingFormBeforeSwitching(selectedCodingForm);

            if (isUpdated) {
              // Show Popup for confirmation
              setCodingFormOnSwitchForSave(values);
              return false;
            }
            return true;
          }}
          onSelectionChange={(values) => {
            setCodingFormUpdated(false);
            setSelectedCodingForm(values);
          }}
          dataTestId="default-coding-form-dropdown"
        />
      </Box>

      <Box className="coding-form-fields-container">
        <DynamicFormFields
          ref={dynamicFormRef}
          fields={documentCodingForm}
          setMessageType={setMessageType}
          showTestButton={false}
          onFieldsChange={(data: CodingFormItem[]) => {
            setUpdatedDocumentCodingForm(data);
            setCodingFormUpdated(true);
          }}
          disableAll={assignmentMode && isDocumentCompleted}
          setCodingFormReady={setCodingFormReady}
          onClear={() => {
            setCodingFormUpdated(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default React.memo(CodingForm);
